import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Heart, Calendar as CalIcon, Plus, Trash2, Check, MapPin, Send, Sparkles, FileSignature, CreditCard, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Public, no-auth intake form. The bride / lead client receives a share
// link from the salon, clicks it, and fills out their event details
// directly. The salon side sees the updates instantly.
export default function PublicEventIntakePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  // Server-known progress: contract status (draft/sent/signed) + deposit status (pending/paid)
  const [progress, setProgress] = useState({ contract_status: "draft", deposit_status: "pending", deposit_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Re-fetch helper so we can poll/refresh after returning from Stripe.
  const refreshEvent = useCallback(async () => {
    const res = await axios.get(`${API}/public/events/intake/${token}`);
    setEvent(prev => prev ? prev : {
      name: res.data.name || "",
      date: res.data.date || "",
      time: res.data.time || "10:00",
      ready_by: res.data.ready_by || "",
      venue: res.data.venue || "",
      party_size: res.data.party_size || 0,
      lead_client: res.data.lead_client || { name: "", phone: "", email: "" },
      roster: res.data.roster || [],
      notes: res.data.notes || "",
      inspo_link: res.data.inspo_link || "",
      dress_color: res.data.dress_color || "",
      allergies: res.data.allergies || "",
    });
    setProgress({
      contract_status: res.data.contract_status || "draft",
      deposit_status: res.data.deposit_status || "pending",
      deposit_amount: res.data.deposit_amount || 0,
    });
    return res.data;
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshEvent();
      } catch (e) {
        if (!cancelled) setError("This link is invalid or has expired. Please contact the salon for a new one.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, refreshEvent]);

  // Stripe roundtrip: when bride returns with ?deposit=success we poll
  // the verify endpoint until paid (or 10 seconds elapse).
  useEffect(() => {
    if (searchParams.get("deposit") !== "success") return;
    let alive = true;
    let elapsed = 0;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/public/events/${token}/deposit/verify`);
        if (!alive) return;
        if (r.data.status === "paid") {
          await refreshEvent();
          return;
        }
      } catch { /* keep polling — Stripe sometimes briefly 404s */ }
      elapsed += 1500;
      if (alive && elapsed < 12000) setTimeout(tick, 1500);
    };
    tick();
    return () => { alive = false; };
  }, [searchParams, token, refreshEvent]);

  const setLead = (k, v) => setEvent(f => ({ ...f, lead_client: { ...f.lead_client, [k]: v } }));
  const updateRoster = (idx, k, v) => {
    const next = [...event.roster];
    next[idx] = { ...next[idx], [k]: v };
    setEvent(f => ({ ...f, roster: next }));
  };
  const addRow = () => setEvent(f => ({ ...f, roster: [...f.roster, { name: "", service: "", start_time: "" }] }));
  const removeRow = (idx) => setEvent(f => ({ ...f, roster: f.roster.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!event.lead_client?.name?.trim()) { setError("Please enter your name"); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/public/events/intake/${token}`, {
        ...event,
        party_size: parseInt(event.party_size || 0, 10) || (event.roster?.length || 0),
      });
      // After submit, refresh from server so the contract/deposit
      // stages reflect server-side status (the salon may already have
      // pushed the contract by the time the bride hits submit).
      await refreshEvent();
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submit failed. Please try again.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50 px-4">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-10 text-center">
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900 mb-2">Link not found</p>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted || progress.contract_status !== "draft") {
    return (
      <ContractStage
        token={token}
        progress={progress}
        eventName={event?.name}
        salonName={event?.lead_client?.name || ""}
        onChanged={refreshEvent}
        showThanks={submitted && progress.contract_status === "draft"}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 py-10 px-4" data-testid="public-intake-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 flex items-center justify-center mb-4 shadow-lg shadow-rose-500/30">
            <Heart className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-500 mb-1">Event Intake</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Tell us about your event
          </h1>
          <p className="text-slate-600 text-sm mt-2 max-w-md mx-auto font-['Manrope']">
            We can&apos;t wait to be part of your day. Fill in as much as you know — you can always update this later.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl p-7 sm:p-9 space-y-6">
          {/* Event basics */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Event basics</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 font-medium">Event name</label>
                <input value={event.name} onChange={e => setEvent(f => ({ ...f, name: e.target.value }))}
                  data-testid="form-name" placeholder="e.g. Sarah & Michael's Wedding"
                  className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 font-medium">Date of Event</label>
                  <input type="date" value={event.date} onChange={e => setEvent(f => ({ ...f, date: e.target.value }))}
                    data-testid="form-date"
                    className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                </div>
                <div>
                  <label className="text-xs text-slate-600 font-medium">Start time</label>
                  <input type="time" value={event.time} onChange={e => setEvent(f => ({ ...f, time: e.target.value }))}
                    data-testid="form-time"
                    className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-slate-600 font-medium">Ready by <span className="text-slate-400 font-normal">— when does the whole party need to be finished?</span></label>
                  <input type="time" value={event.ready_by || ""} onChange={e => setEvent(f => ({ ...f, ready_by: e.target.value }))}
                    data-testid="form-ready-by"
                    className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Venue / Address</label>
                <input value={event.venue} onChange={e => setEvent(f => ({ ...f, venue: e.target.value }))}
                  data-testid="form-venue" placeholder="Where will we be?"
                  className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
              </div>
            </div>
          </div>

          {/* Lead contact */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Your contact info</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={event.lead_client.name} onChange={e => setLead("name", e.target.value)}
                placeholder="Your name *" data-testid="form-lead-name" required
                className="h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
              <input value={event.lead_client.phone} onChange={e => setLead("phone", e.target.value)}
                placeholder="Phone" data-testid="form-lead-phone"
                className="h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
              <input value={event.lead_client.email} onChange={e => setLead("email", e.target.value)}
                placeholder="Email" type="email" data-testid="form-lead-email"
                className="h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
          </div>

          {/* Roster */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Who&apos;s getting styled? ({event.roster.length})</p>
              <button type="button" onClick={addRow} data-testid="form-add-row"
                className="text-xs inline-flex items-center gap-1 px-3 h-8 rounded-full bg-slate-900 text-white hover:bg-slate-800">
                <Plus className="w-3 h-3" /> Add person
              </button>
            </div>
            <div className="space-y-2">
              {event.roster.length === 0 && (
                <p className="text-xs text-slate-400 italic">Add each person and the service they&apos;d like (updo, makeup, nails…)</p>
              )}
              {event.roster.map((row, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`form-row-${idx}`}>
                  <input value={row.name || ""} onChange={e => updateRoster(idx, "name", e.target.value)}
                    placeholder="Name" className="col-span-4 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                  <input value={row.service || ""} onChange={e => updateRoster(idx, "service", e.target.value)}
                    placeholder="Service (updo, makeup…)" className="col-span-5 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                  <input type="time" value={row.start_time || ""} onChange={e => updateRoster(idx, "start_time", e.target.value)}
                    className="col-span-2 h-10 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                  <button type="button" onClick={() => removeRow(idx)}
                    className="col-span-1 h-10 rounded-lg hover:bg-rose-50 flex items-center justify-center">
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Vibe */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Inspo link (Pinterest, IG…)</label>
              <input value={event.inspo_link} onChange={e => setEvent(f => ({ ...f, inspo_link: e.target.value }))}
                data-testid="form-inspo" placeholder="https://…"
                className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Dress / theme color</label>
              <input value={event.dress_color} onChange={e => setEvent(f => ({ ...f, dress_color: e.target.value }))}
                data-testid="form-dress" placeholder="Ivory, blush, navy…"
                className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 font-medium">Allergies / sensitivities</label>
            <input value={event.allergies} onChange={e => setEvent(f => ({ ...f, allergies: e.target.value }))}
              data-testid="form-allergies" placeholder="(none)"
              className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
          </div>

          <div>
            <label className="text-xs text-slate-600 font-medium">Anything else we should know?</label>
            <textarea value={event.notes} onChange={e => setEvent(f => ({ ...f, notes: e.target.value.slice(0, 2000) }))}
              data-testid="form-notes" rows={3}
              placeholder="Trial run requests, must-have looks, special touches…"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300 resize-none" />
          </div>

          {error && (
            <p className="text-rose-600 text-sm font-medium" data-testid="form-error">{error}</p>
          )}

          <button type="submit" disabled={submitting} data-testid="form-submit"
            className="w-full h-12 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Send className="w-4 h-4" />
            {submitting ? "Sending…" : "Send to salon"}
          </button>
          <p className="text-[11px] text-slate-400 text-center font-['Manrope']">
            Your info is sent only to the salon. We&apos;ll never share it.
          </p>
        </form>
      </div>
    </div>
  );
}


// ─── Contract + Deposit stage ─────────────────────────────────────
// Shown after the bride submits her intake, OR if the salon has
// already pushed the contract before she returned. Three phases:
//   1. Contract is `sent` → bride reads + types her signature
//   2. Contract is `signed` and deposit is `pending` → "Pay deposit"
//   3. Both done → confirmation card
function ContractStage({ token, progress, eventName, onChanged, showThanks }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState("");
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState("");
  const [payError, setPayError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/events/${token}/contract`);
        if (alive) setContract(r.data);
      } catch {
        if (alive) setContract({ rendered: "", status: "draft" });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, progress.contract_status]);

  const sign = async () => {
    if (typedName.trim().length < 2) { setSignError("Type your full name to sign"); return; }
    setSigning(true);
    setSignError("");
    try {
      await axios.post(`${API}/public/events/${token}/contract/sign`, { signed_by_name: typedName.trim() });
      await onChanged?.();
      const r = await axios.get(`${API}/public/events/${token}/contract`);
      setContract(r.data);
    } catch (err) {
      setSignError(err?.response?.data?.detail || "Could not sign. Please try again.");
    }
    setSigning(false);
  };

  const startPayment = async () => {
    setPaying(true);
    setPayError("");
    try {
      const r = await axios.post(`${API}/public/events/${token}/deposit/checkout`, {
        origin_url: window.location.origin,
      });
      // Hand off to Stripe Checkout. Stripe redirects back with
      // ?deposit=success which the parent component polls.
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setPayError(err?.response?.data?.detail || "Payment could not start. Please try again.");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const fullyDone = progress.contract_status === "signed" && progress.deposit_status === "paid";

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 py-10 px-4" data-testid="contract-stage">
      <div className="max-w-2xl mx-auto">
        {showThanks && progress.contract_status === "draft" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-5 text-center">
            <p className="text-sm text-emerald-800">
              <Check className="w-4 h-4 inline mr-1" />
              Details received! The salon will send your contract shortly.
            </p>
          </div>
        )}

        {fullyDone ? (
          <div className="bg-white rounded-3xl shadow-xl p-10 text-center" data-testid="fully-booked">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500 flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30">
              <Check className="w-8 h-8 text-white" strokeWidth={3} />
            </div>
            <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 mb-2">You&apos;re booked!</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Contract signed and deposit received. We can&apos;t wait to make {eventName || "your event"} unforgettable.
            </p>
            <p className="text-xs text-slate-400 mt-4">A receipt has been sent to your email.</p>
          </div>
        ) : (
          <>
            {/* Phase 1: review + sign contract */}
            <div className="bg-white rounded-3xl shadow-xl p-7 sm:p-9" data-testid="contract-card">
              <div className="flex items-center gap-2 mb-3">
                <FileSignature className="w-5 h-5 text-rose-600" />
                <h2 className="font-['Outfit'] text-2xl font-semibold text-slate-900">
                  {progress.contract_status === "signed" ? "Contract signed" : "Review & sign contract"}
                </h2>
                {progress.contract_status === "signed" && (
                  <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold bg-emerald-50 text-emerald-700">Signed</span>
                )}
              </div>

              <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-5 max-h-[420px] overflow-y-auto"
                data-testid="contract-text">
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-['Manrope'] leading-relaxed">{contract?.rendered || "Contract is being prepared by the salon."}</pre>
              </div>

              {progress.contract_status === "sent" && (
                <div className="mt-5 space-y-3">
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Sign by typing your full name <span className="text-rose-600">*</span></span>
                    <input value={typedName} onChange={e => setTypedName(e.target.value)}
                      data-testid="sign-name-input"
                      placeholder="e.g. Sarah Smith"
                      className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 italic text-slate-700 focus:outline-none focus:border-rose-300" />
                  </label>
                  {signError && <p className="text-rose-600 text-sm">{signError}</p>}
                  <button onClick={sign} disabled={signing}
                    data-testid="sign-contract-btn"
                    className="w-full h-12 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                    <FileSignature className="w-4 h-4" />
                    {signing ? "Signing…" : "Sign contract"}
                  </button>
                </div>
              )}

              {progress.contract_status === "signed" && contract?.signed_by_name && (
                <div className="mt-4 bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm">
                  <p className="text-emerald-800">
                    Signed by <strong>{contract.signed_by_name}</strong> on{" "}
                    {contract.signed_at ? new Date(contract.signed_at).toLocaleString() : ""}
                  </p>
                </div>
              )}
            </div>

            {/* Phase 2: deposit */}
            {progress.contract_status === "signed" && progress.deposit_status !== "paid" && (
              <div className="bg-white rounded-3xl shadow-xl p-7 sm:p-9 mt-5" data-testid="deposit-card">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-5 h-5 text-rose-600" />
                  <h2 className="font-['Outfit'] text-2xl font-semibold text-slate-900">Pay your deposit</h2>
                </div>
                <p className="text-sm text-slate-600">
                  Your event is reserved once your deposit is received. The deposit is applied to your final balance.
                </p>
                <div className="my-5 bg-gradient-to-br from-rose-50 to-amber-50 rounded-2xl p-5 text-center border border-rose-100">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-rose-600">Deposit due</p>
                  <p className="font-['Outfit'] text-5xl font-semibold text-slate-900 my-2 tabular-nums">
                    ${Number(progress.deposit_amount || 0).toFixed(2)}
                  </p>
                  <p className="text-[11px] text-slate-500">Non-refundable</p>
                </div>
                {payError && <p className="text-rose-600 text-sm mb-3">{payError}</p>}
                <button onClick={startPayment} disabled={paying}
                  data-testid="pay-deposit-btn"
                  className="w-full h-12 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" />
                  {paying ? "Redirecting to Stripe…" : "Pay deposit securely"}
                </button>
                <p className="text-[10px] text-slate-400 text-center mt-3 font-['Manrope']">
                  Powered by Stripe. Your card details never touch our servers.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
